import { alpha, Box, type BoxProps, styled } from '@mui/material'
import type { ReactNode } from 'react'

import { RADIUS } from '@/shared/styles/radii'
import { scaledFontSize, typeScale } from '@/shared/styles/typography'

export interface TabItem<T extends string = string> {
  key: T
  label: ReactNode
  icon?: ReactNode
}

interface Props<T extends string = string> extends Omit<BoxProps, 'onChange'> {
  tabs: TabItem<T>[]
  active: T
  onChange: (key: T) => void
  rightSlot?: ReactNode
}

// 共通層に置き、 録音ブロック (apps/latest) と指導前準備のカード (features/guidance) が同じ部品を使う.
// features 層は apps/* の colorTokens を import できないので (.eslintrc.cjs no-restricted-imports)、
// 色は theme の palette で引く (apps/latest の theme が colorTokens を写している):
//   text.primary = fg.default / text.secondary = fg.muted / action.selected = bg.muted /
//   secondary.light = bg.emphasis / divider = border.default
const Bar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '6px 12px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: 44,
}))

// tablist が持てるのは tab だけ (WAI-ARIA). 右端 (経過時間・再取得) は tablist の外に並べる.
const TabList = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
})

// 実 button で描く (tab-pill.md): div + role="tab" だけでは focus 到達性がなく、
// キーボードで切替できない.
const Tab = styled('button', {
  shouldForwardProp: (p) => p !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  appearance: 'none',
  border: 'none',
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  // 文字が拡大したら器も伸ばす. 固定 height だと「大」(×1.5) で文字が器からはみ出す
  // (scale=1 では文字の行box < 28px なので minHeight が勝ち、見た目は従来と同一).
  minHeight: 28,
  padding: '0 10px',
  borderRadius: RADIUS.md,
  // 録音 / SOAP 画面は文字サイズ 3 値の追従スコープ. styled に静的オブジェクトを
  // 渡すと scaledFontSize の callback が評価されず追従が黙って壊れるため theme を
  // 引数で受ける (font-scale.md 禁則).
  fontSize: scaledFontSize('label')(theme),
  fontWeight: typeScale.label.fontWeight,
  // button の UA 既定は line-height: normal で、div 時代の継承値と揃わない. spec の値を明示.
  lineHeight: typeScale.label.lineHeight,
  cursor: 'pointer',
  transition: 'background-color 120ms',
  background: active ? theme.palette.action.selected : 'transparent',
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  '&:hover': active ? undefined : { background: theme.palette.secondary.light },
  '&:focus': { outline: 'none' },
  // タブは無彩色 chrome なので focus は neutral ring (= focus.ringNeutral。青 ring はコミット操作用).
  '&:focus-visible': {
    boxShadow: `0 0 0 2px ${alpha(theme.palette.text.primary, 0.2)}`,
  },
}))

export const TabBar = <T extends string = string>({
  tabs,
  active,
  onChange,
  rightSlot,
  ...rest
}: Props<T>) => (
  <Bar {...rest}>
    <TabList role="tablist">
      {tabs.map((t) => (
        <Tab
          key={t.key}
          type="button"
          active={t.key === active}
          onClick={() => onChange(t.key)}
          role="tab"
          aria-selected={t.key === active}
        >
          {t.icon}
          {t.label}
        </Tab>
      ))}
    </TabList>
    {/* false / null / undefined のときは箱ごと出さない (呼び出し側は `cond && <X/>` で渡す) */}
    {rightSlot ? (
      <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
        {rightSlot}
      </Box>
    ) : null}
  </Bar>
)
